import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DEFAULTS_PRESETS_SCHEDULED } from '../ph-datepicker/ph-datepicker/preset';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';

// models
import { IConfirmModalAnswer } from '../../modals/confirm-modal/models/confirmModalAnswer';
import { IConfirmModalData } from '../../modals/confirm-modal/models/confirmModalData';
import { IStandExecutionFilters } from './models/standExecutionFilters';
import { IStandExecutionData } from './models/standExecutionData';
import { IExecuteEvent } from './models/executeEvent';
import { IProperty } from '../../../planogram/models/product-base';
import { IUniversalDateModalData } from '../../modals/complex-deadline-modal/models/universalDateModalData';
import { DwUserActivityNames } from '../../models/dwUserActivityNames';
import { DwUserActivityTypes } from '../../models/dwUsersActivityTypes';

// components
import { ComplexDeadlineModalComponent } from '../../modals/complex-deadline-modal/complex-deadline-modal.component';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { FilterProductsModalComponent } from '../../modals/filter-products-modal/filter-products-modal.component';

// services
import { PlanogramsService } from '../../services/planograms.service';
import { SendExecutionService } from '../../services/send-execution.service';
import { SessionService } from '../../services/session.service';
import { StoresService } from '../../services/stores.service';
import { Utils } from '../../services/utils.service';
import { DwUsersActivityService } from '../../services/dw-users-activity.service';

import { SHARED_MODULES } from '../../../shared.';
import { GetLinkPipe } from '../../pipes/get-link.pipe';
import { TruncatePipe } from '../../pipes/truncate.pipe';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhLoaderComponent } from '../ph-loader/ph-loader.component';
import { SearchComponent } from '../search/search.component';
import { TableComponent } from '../table/table.component';

const DATE_FORMAT = 'DD.MM.YYYY',
	EXECUTE_COLUMNS: Array<string> = [
		'SHOP_NAME',
		'SCHEDULED_PLANOGRAMS.DATE',
		'DEADLINE.MODAL.DOING_TO',
		'TIME_TRACKER',
		'FORMAT_NAME',
		'STORE_MANAGER',
		'LAST_EXECUTION_DATE'
	],
	TOOLTIP_DEADLINE = 'DEADLINE.ALREADY_HAVE';

@Component({
    selector: 'app-multi-execution',
    templateUrl: './multi-execution.component.html',
    styleUrls: ['./multi-execution.component.scss'],
	imports: [
		SHARED_MODULES,
		GetLinkPipe,
		TruncatePipe,
		InfiniteScrollDirective,
		FaIconComponent,
		PhButtonComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		PhLoaderComponent,
		SearchComponent,
		TableComponent
	],
    standalone: true
})
export class MultiExecutionComponent implements OnInit, OnDestroy {
	stands: Array<number> = [];
	filters: IStandExecutionFilters = { search: '', markers: [] };

	original_data: Array<IStandExecutionData> = [];
	visible_data: Array<IStandExecutionData> = [];
	template_data: Array<IStandExecutionData> = [];

	stands_to_execute: any = [];
	page: number = 1;

	showLoader: boolean = false;
	table_page: number;
	table_sorting: { index: number, order: string, tableIdx: number };
	all_selected: boolean = false;
	disableSelectAll: boolean = false;
	isTimeTrackerActive: boolean = false;

	storeMarkers: Array<any> = [];

	minPossibleScheduledDate: Date | null = null;
	maxPossibleScheduledDate: Date | null = null;
	minPossibleDeadlineDate: Date | null = null;
	maxPossibleDeadlineDate: Date | null = null;

	disableScheduledUpdateAllToggle: boolean = false;
	disableDeadlineUpdateAllToggle: boolean = false;

	@Input('plan') plan: { id: string | number, name: string };
	@Input('params') params: any;
	@Input('stands') set setInitialData(value) {
		this.stands = value.map(s => s.main_stand);
	}

	@Input('filters') set setFilters(value) {
		this.filters = Object.assign({}, value);

		if (this.original_data.length && !this.showLoader) {
			this.ngOnInit();
		}
	}

	@Output('cancel') cancelEvent: EventEmitter<any> = new EventEmitter<any>();
	@Output('execute') executeEvent: EventEmitter<IExecuteEvent> = new EventEmitter<IExecuteEvent>();

	static formatExecuteTableData(stand, selected_stands: any[], planogram_id?: string | number, sendExecutionService?: any) {
		return {
			columns: EXECUTE_COLUMNS,
			data: stand.planograms
				.sort((a, b) =>
					(b.managers && b.managers.length && b.managers.every(m => m.id) && b.planogram_active) -
					(a.managers && a.managers.length && a.managers.every(m => m.id) && a.planogram_active) ||
					(b.shop_id && b.planogram_active) - (a.shop_id && b.planogram_active)
				)
				.map(s => {
					const isDisabled = (!s.shop_id || !s.managers || !s.managers.length || s.managers.some(m => !m.id) || !s.planogram_active);

					let scheduledMinDate = new Date();
					scheduledMinDate.setDate(scheduledMinDate.getDate() + 1);
					let scheduledMaxDate = s['deadline_date'] ? s['deadline_date'].toString() : null;

					let deadlineMinDate = s.scheduled_date ? s.scheduled_date.toString() : new Date().toString();
					let deadlineMaxDate = null;

					if (sendExecutionService?.scheduled.updateForAll) {
						scheduledMinDate = new Date();
						scheduledMinDate.setDate(scheduledMinDate.getDate() + 1);
						scheduledMaxDate = null;
					}
					if (sendExecutionService?.deadline.updateForAll) {
						deadlineMaxDate = null;
					}

					const row = [
						{
							data: s.shop_name,
							stand_id: s.main_stand_id,
							planogram_id: s.id
						},
						{
							data: {
								date: s.scheduled_date,
								datepicker: true,
								type: 'scheduled',
								placeholder: 'SCHEDULED_PLANOGRAMS.IN_TIME',
								tooltip: s.scheduled_date && !s.scheduled_changed ? TOOLTIP_DEADLINE : null,
								min_date: scheduledMinDate.toString(),
								max_date: scheduledMaxDate,
								disabled: isDisabled,
								presets: DEFAULTS_PRESETS_SCHEDULED
							}
						},
						{
							data: {
								date: s.deadline_date,
								datepicker: true,
								type: 'deadline',
								tooltip: s.deadline_date && !s.deadline_changed ? TOOLTIP_DEADLINE : null,
								min_date: deadlineMinDate,
								max_date: deadlineMaxDate,
								disabled: isDisabled
							}
						},
						{
							data: {
								toggle: true,
								value: s.enable_time_tracking,
								disabled: isDisabled
							}
						},
						{
							data: s.markers?.map(marker => marker.name).join(', ') || ' - '
						},
						{
							data: { managers: s.managers, select: true, disabled: isDisabled }
						},
						{
							data: s.updated_at ? moment(s.updated_at).format(DATE_FORMAT) : ' - '
						}
					] as any;

					row.checked = selected_stands.some(
						selected => selected.main_stand_id == s.main_stand_id && selected.shops.some(shop => shop.planogram == s.id)
					);

					if (planogram_id) {
						row.checked = s.id == planogram_id;
					}

					row.disabled = isDisabled;

					return row;
				})
		};
	}

	static isValid(data) {
		return data.some(i => !i.disabled);
	}

	constructor(
		private dialog: MatDialog,
		private planogramsService: PlanogramsService,
		private sendExecutionService: SendExecutionService,
		private storesService: StoresService,
		private sessionService: SessionService,
		private dwUsersActivityService: DwUsersActivityService
	) {}

	async ngOnInit() {
		this.dwUsersActivityService.track(DwUserActivityNames.SendToExecution, DwUserActivityTypes.ChangeRoute);

		let data: IStandExecutionData[],
			firstLoad: boolean = true;

		if (!this.original_data.length) {
			this.showLoader = true;
			data = await this.planogramsService.getExecutionData(this.params);
			data.forEach(d => d.planograms.forEach(p => {
				if (p.managers && p.managers.length) {
					p.managers.map(m => m.active = false);
					p.selected_manager = p.managers[0];
				}
				p.disabled = !p.managers || !p.managers.length || !p.managers.every(m => m.id) || !p.planogram_active;
			}));

			this.original_data = data;
		} else {
			data = Utils.deepCopy(this.original_data) as IStandExecutionData[];
			firstLoad = false;
		}

		if (!this.storeMarkers.length) this.storeMarkers = await this.storesService.getShopMarkers();

		const currentSelection = this.stands_to_execute.map(s => ({
			main_stand_id: s.main_stand_id,
			shops: s.shops.map(shop => ({ id: shop.id, planogram: shop.planogram }))
		}));

		this.visible_data = data.map(stand => {
			stand.planograms.forEach(plan => {
				if (this.visible_data.length) {
					const originalStand = this.original_data.find(d => d.main_stand_id === stand.main_stand_id);
					const originalPlan = originalStand?.planograms.find(pl => pl.id === plan.id);

					if (originalPlan) {
						plan.selected_manager = originalPlan.selected_manager;
						plan.scheduled_date = originalPlan.scheduled_date;
						plan.deadline_date = originalPlan.deadline_date;
						plan.scheduled_changed = originalPlan.scheduled_changed;
						plan.deadline_changed = originalPlan.deadline_changed;
						plan.enable_time_tracking = originalPlan.enable_time_tracking;

						if (plan.managers && plan.managers.length) {
							plan.managers.forEach(manager => {
								manager.active = manager.id === originalPlan.selected_manager?.id;
							});
						}
					}
				}
			});

			if (this.filters.search) {
				stand.planograms = stand.planograms.filter(s =>
					Utils.searchItem(
						s,
						['shop_name'],
						this.filters.search,
						this.sessionService.personalSettings.searchAlgorithm
					) ||
					s.managers?.some(m =>
						Utils.searchItem(
							m,
							['name'],
							this.filters.search,
							this.sessionService.personalSettings.searchAlgorithm
						)
					)
				);
			}

			if (this.filters.markers.length) {
				const markerIds: Array<number> = this.filters.markers.map((marker: IProperty): number => marker.id);
				stand.planograms = stand.planograms.filter(plan =>
					plan.markers.some((marker: IProperty): boolean => this.checkMarkersChildren(marker.id, markerIds))
				);
			}

			return stand;
		});

		this.visible_data.forEach(s => {
			s.tableData = MultiExecutionComponent.formatExecuteTableData(
				s, this.stands_to_execute, firstLoad ? this.plan?.id : null
			);
			s.isValid = MultiExecutionComponent.isValid(s.tableData.data);

			s.tableData.data.forEach((row, rowIndex) => {
				const planogram = s.planograms[rowIndex];
				if (planogram && row[1]?.data && row[2]?.data) {
					let scheduledMinDate = new Date();
					scheduledMinDate.setDate(scheduledMinDate.getDate() + 1);
					row[1].data.min_date = scheduledMinDate.toString();
					row[1].data.max_date = planogram.deadline_date ? planogram.deadline_date.toString() : null;

					row[2].data.min_date = planogram.scheduled_date ? planogram.scheduled_date.toString() : new Date().toString();
				}
			});
		});

		if (!firstLoad && currentSelection.length > 0) {
			currentSelection.forEach(selectedStand => {
				const visibleStand = this.visible_data.find(s => s.main_stand_id === selectedStand.main_stand_id);
				if (visibleStand) {
					selectedStand.shops.forEach(selectedShop => {
						const planogram = visibleStand.planograms.find(p => p.id === selectedShop.planogram);
						if (planogram && !planogram.disabled) {
							this.selectPlanogram(visibleStand, planogram, true);
						}
					});
				}
			});
		}

		if (this.table_sorting) {
			this.visible_data.forEach((s, idx) => {
				if (idx !== this.table_sorting.tableIdx) return;

				s.tableData.data.sort(
					(a, b) => {
						const aDisabled = a.disabled,
							bDisabled = b.disabled;

						return Utils.sortPlanogramData({
							firstEl: a[this.table_sorting.index].data,
							secondEl: b[this.table_sorting.index].data,
							order: this.table_sorting.order,
							displayValue: s.tableData.columns[this.table_sorting.index],
							dateFormat: DATE_FORMAT,
							disabledFirstEl: aDisabled,
							disabledSecondEl: bDisabled
						});
					}
				);
			});
		}

		if (firstLoad) {
			this.checkDefaultSelected();
		}

		this.disableSelectAll = !this.visible_data.length ||
			this.visible_data.every(stand => !stand.planograms.length) ||
			this.visible_data.every(stand => stand.planograms.every(p => !p.managers || !p.managers.length || !p.managers.every(m => m.id) || !p.planogram_active));

		this.template_data = this.visible_data.slice(0, 10 * this.page);
		this.checkAllSelected();
		this.checkAllTimeTrackersSelected();
		this.calculateBulkActionDateRestrictions();

		this.showLoader = false;
	}

	private selectPlanogram(stand: IStandExecutionData, planogram: any, updateTableData: boolean = true) {
		let stand_to_execute = this.stands_to_execute.find(s => s.main_stand_id == stand.main_stand_id);

		if (!stand_to_execute) {
			stand_to_execute = {
				main_stand_id: stand.main_stand_id,
				name: stand.name,
				comment: stand.comment,
				shops: []
			};
			this.stands_to_execute.push(stand_to_execute);
		}

		const existingShop = stand_to_execute.shops.find(s => s.planogram === planogram.id);
		if (existingShop) return;

		stand_to_execute.shops.push({
			id: planogram.main_stand_id,
			planogram: planogram.id,
			shop_name: planogram.shop_name,
			stand_name: stand.name,
			manager_id: planogram.selected_manager.id,
			manager_name: planogram.selected_manager.name,
			scheduled_date: planogram.scheduled_date,
			deadline_date: planogram.deadline_date,
			deadline_overdue: planogram.deadline_date && Utils.dateDayComparison(new Date(), new Date(planogram.deadline_date)),
			timeTracker: planogram.enable_time_tracking
		});

		if (updateTableData) {
			stand.tableData = MultiExecutionComponent.formatExecuteTableData(stand, this.stands_to_execute);
			stand.isValid = MultiExecutionComponent.isValid(stand.tableData.data);
		}
	}

	onFiltersCancel() {
		this.filters.markers = [];
		this.ngOnInit();
	}

	onSearch(value: string) {
		this.filters.search = value;
		this.ngOnInit();
	}

	openExecutionFilterModal() {
		const dialogRef = this.dialog.open(FilterProductsModalComponent, {
			width: '40%',
			height: 'auto',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				shopMarkers: this.storeMarkers,
				checked_markers: this.filters.markers,
				shopGroups: [],
				checked_groups: [],
				type: 'plans'
			}
		});

		dialogRef.afterClosed().subscribe(result => {

			if (result === true) return this.onFiltersCancel();

			if (!result) return;

			this.filters.markers = result.markers;
			this.ngOnInit();
		});
	}

	checkMarkersChildren(id: number, filterIds: Array<number>): boolean {
		if (filterIds.includes(id) || filterIds.includes(-1)) return true;
		return filterIds.some((filterId: number) => {
			const children: Array<number> = this.storeMarkers.find((marker) => marker.id === filterId)?.children.map((marker) => marker.id) || [];
			if (!children.length) return false;
			return this.checkMarkersChildren(id, children);
		});
	}

	scrollDown() {
		if (this.showLoader) return;

		++this.page;

		this.template_data = this.visible_data.slice(0, 10 * this.page);
	}

	checkDefaultSelected() {
		if (!this.plan) return;

		this.stands_to_execute = this.original_data
			.filter(s => s.planograms.some(p => p.id == this.plan?.id))
			.map(s => ({
				main_stand_id: s.main_stand_id,
				name: s.name,
				comment: s.comment,
				shops: s.planograms
					.filter(p => p.id == this.plan?.id)
					.map(p => ({
						id: s.main_stand_id,
						planogram: p.id,
						shop_name: p.shop_name,
						stand_name: s.name,
						manager_id: p.selected_manager.id,
						manager_name: p.selected_manager.name,
						scheduled_date: p.scheduled_date,
						deadline_date: p.deadline_date,
						deadline_overdue: p.deadline_date && Utils.dateDayComparison(new Date(), new Date(p.deadline_date)),
						timeTracker: p.enable_time_tracking
					}))
			}));
	}

	remove(idx) {
		const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '574px',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				twoAnswers: true,
				label: 'CONFIRM_DELETE_EXECUTION_STAND_MESSAGE',
				confirmLabel: 'YES',
				cancelLabel: 'NO'
			}
		});

		dialogRef.afterClosed().subscribe((result: IConfirmModalAnswer) => {
			if (!result.answer) return;

			const stand_to_execute = this.original_data[idx].main_stand_id;

			this.original_data.splice(idx, 1);
			this.stands_to_execute = this.stands_to_execute.filter(s => s.main_stand_id != stand_to_execute);

			if (!this.original_data.length) {
				return this.cancelEvent.emit(true);
			}

			return this.ngOnInit();
		});
	}

	removeShop(parent_idx, child_idx) {
		if (!this.stands_to_execute[parent_idx]) return;

		this.stands_to_execute[parent_idx].shops.splice(child_idx, 1);

		const stand_to_unselect = this.visible_data.find(s => s.main_stand_id == this.stands_to_execute[parent_idx].main_stand_id);

		if (stand_to_unselect) {
			(stand_to_unselect.tableData.data[child_idx] || {}).checked = false;
			stand_to_unselect.tableData = Object.assign({}, stand_to_unselect.tableData);
		}

		if (!this.stands_to_execute[parent_idx].shops.length) {
			this.stands_to_execute.splice(parent_idx, 1);
			this.all_selected = false;
		}

		this.calculateBulkActionDateRestrictions();
		return this.ngOnInit();
	}

	removeStand(idx) {
		const stand_to_unselect = this.visible_data.find(s => s.main_stand_id == this.stands_to_execute[idx].main_stand_id);

		if (stand_to_unselect) {
			stand_to_unselect.tableData.data.forEach(row => row.checked = false);
			stand_to_unselect.tableData = Object.assign({}, stand_to_unselect.tableData);
		}

		this.stands_to_execute.splice(idx, 1);

		this.all_selected = false;
		this.calculateBulkActionDateRestrictions();

		return this.ngOnInit();
	}

	onSelected(row, stand) {
		let { value } = row.data[0];

		const standIndex = this.original_data.findIndex(s => s.main_stand_id == value.stand_id);
		const planogramIndex = this.visible_data[standIndex].planograms.findIndex(p => p.id == value.planogram_id);
		const planogram = stand.planograms[planogramIndex];

		this.selectPlanogram(stand, planogram);
		this.checkAllSelected();
		this.calculateBulkActionDateRestrictions();
	}

	checkAllSelected(): void {
		const visibleValid = [];
		this.visible_data.forEach(s =>
			s.planograms.forEach(p => {
				if (p.shop_id && p.managers?.length && p.managers.every(m => m.id) && p.planogram_active) {
					visibleValid.push({ sId: s.main_stand_id, pId: p.id });
				}
			})
		);

		if (!visibleValid.length) {
			this.all_selected = false;
			return;
		}

		this.all_selected = visibleValid.every(v =>
			this.stands_to_execute.find(s => s.main_stand_id === v.sId)?.shops.some(shop => shop.planogram === v.pId)
		);
	}

	selectAll(): void {
		if (this.disableSelectAll) return;

		if (this.all_selected) {
			this.all_selected = false;
			this.visible_data.forEach(vStand => {
				const execStand = this.stands_to_execute.find(s => s.main_stand_id === vStand.main_stand_id);
				if (execStand) {
					const visibleIds = vStand.planograms.map(p => p.id);
					execStand.shops = execStand.shops.filter(s => !visibleIds.includes(s.planogram));
				}
			});
			this.stands_to_execute = this.stands_to_execute.filter(s => s.shops.length);
		} else {
			this.all_selected = true;
			this.visible_data.forEach(stand => {
				const validPlanograms = stand.planograms.filter(p =>
					p.shop_id && p.managers?.length && p.managers.every(m => m.id) && p.planogram_active
				);

				if (validPlanograms.length) {
					let stand_to_exec = this.stands_to_execute.find(s => s.main_stand_id === stand.main_stand_id);
					if (!stand_to_exec) {
						stand_to_exec = {
							main_stand_id: stand.main_stand_id,
							name: stand.name,
							comment: stand.comment,
							shops: []
						};
						this.stands_to_execute.push(stand_to_exec);
					}

					validPlanograms.forEach(p => {
						if (!stand_to_exec.shops.some(s => s.planogram === p.id)) {
							stand_to_exec.shops.push({
								id: p.main_stand_id,
								planogram: p.id,
								shop_name: p.shop_name,
								stand_name: stand.name,
								manager_id: p.selected_manager.id,
								manager_name: p.selected_manager.name,
								scheduled_date: p.scheduled_date,
								deadline_date: p.deadline_date,
								deadline_overdue: p.deadline_date && Utils.dateDayComparison(new Date(), new Date(p.deadline_date)),
								timeTracker: p.enable_time_tracking
							});
						}
					});
				}
			});
		}

		this.calculateBulkActionDateRestrictions();

		this.visible_data.forEach(stand => {
			stand.tableData = MultiExecutionComponent.formatExecuteTableData(stand, this.stands_to_execute);
			stand.isValid = MultiExecutionComponent.isValid(stand.tableData.data);
		});

		this.template_data = this.visible_data.slice(0, 10 * this.page);
	}

	onUnSelected(row, stand) {
		const { value } = row.data[0],
			unselect_values = {
				stand: value.stand_id,
				planogram: value.planogram_id
			},
			stand_to_execute = this.stands_to_execute.find(s => s.main_stand_id == stand.main_stand_id);

		if (!stand_to_execute || !Array.isArray(stand_to_execute.shops)) return;

		const visible_data_item = this.visible_data.find(vs => vs.main_stand_id == stand_to_execute.main_stand_id);

		stand_to_execute.shops = stand_to_execute.shops.filter(s => s.id !== unselect_values.stand || s.planogram !== unselect_values.planogram);

		if (!stand_to_execute.shops.length) {
			this.stands_to_execute = this.stands_to_execute.filter(s => s.main_stand_id != stand_to_execute.main_stand_id);
		}

		visible_data_item.tableData = MultiExecutionComponent.formatExecuteTableData(visible_data_item, this.stands_to_execute);
		visible_data_item.isValid = MultiExecutionComponent.isValid(visible_data_item.tableData.data);

		this.checkAllSelected();
		this.calculateBulkActionDateRestrictions();
	}

	onSelectedAll(selected: boolean, stand: IStandExecutionData, check_all: boolean = true) {
		let stand_to_execute = this.stands_to_execute.find(s => s.main_stand_id == stand.main_stand_id),
			existing = !!stand_to_execute;

		if (!stand_to_execute) {
			stand_to_execute = {
				main_stand_id: stand.main_stand_id,
				name: stand.name,
				comment: stand.comment,
				shops: []
			};
		}

		if (selected) {
			const shops = stand.planograms.filter(s => s.shop_id && s.managers && s.managers.length && s.managers.every(m => m.id) && s.planogram_active);
			if (!shops.length) return;
			stand_to_execute.shops = shops.map(s => ({
				id: s.main_stand_id,
				planogram: s.id,
				shop_name: s.shop_name,
				stand_name: stand.name,
				manager_id: s.selected_manager.id,
				manager_name: s.selected_manager.name,
				scheduled_date: s.scheduled_date,
				deadline_date: s.deadline_date,
				deadline_overdue: s.deadline_date && Utils.dateDayComparison(new Date(), new Date(s.deadline_date)),
				timeTracker: s.enable_time_tracking
			}));

			if (!existing) {
				this.stands_to_execute.push(stand_to_execute);
			}
		} else {
			this.stands_to_execute = this.stands_to_execute.filter(s => s != stand_to_execute);
		}

		if (check_all) {
			this.checkAllSelected();
			this.calculateBulkActionDateRestrictions();
		}
	}

	onSelectManager(event, i: number) {
		const standIndexV = this.visible_data.findIndex(s => s.main_stand_id == event.stand),
			planogramIndexV = this.visible_data[standIndexV].planograms.findIndex(p => p.id == event.planogram);

		const standIndexO = this.original_data.findIndex(s => s.main_stand_id == event.stand),
			planogramIndexO = this.original_data[standIndexO].planograms.findIndex(p => p.id == event.planogram);

		const selectedManager = {
			id: event.manager,
			name: this.visible_data[standIndexV].planograms[planogramIndexV].managers.find(m => m.id == event.manager).name,
			active: true
		};

		this.visible_data[standIndexV].planograms[planogramIndexV].selected_manager = selectedManager;
		this.visible_data[standIndexV].planograms[planogramIndexV].managers.forEach(m => {
			m.active = m.id == selectedManager.id;
		});

		this.original_data[standIndexO].planograms[planogramIndexO].selected_manager = selectedManager;
		this.original_data[standIndexO].planograms[planogramIndexO].managers.forEach(m => {
			m.active = m.id == selectedManager.id;
		});

		if (this.stands_to_execute.some(s => s.main_stand_id == event.stand)) {
			this.stands_to_execute.find(s => s.main_stand_id == event.stand)
				.shops.forEach(s => {
				if (s.planogram == event.planogram) {
					s.manager_id = selectedManager.id;
					s.manager_name = selectedManager.name;
				}
			});
		}
	}

	onSelectScheduledDate(event, i: number) {
		const standIndexV = this.visible_data.findIndex(s => s.main_stand_id == event.stand),
			planogramIndexV = this.visible_data[standIndexV].planograms.findIndex(p => p.id == event.planogram);

		const standIndexO = this.original_data.findIndex(s => s.main_stand_id == event.stand),
			planogramIndexO = this.original_data[standIndexO].planograms.findIndex(p => p.id == event.planogram);

		const newScheduledDate = event.date?.toString() || null;

		this.visible_data[standIndexV].planograms[planogramIndexV].scheduled_date = newScheduledDate;
		this.visible_data[standIndexV].planograms[planogramIndexV].scheduled_changed = true;
		this.original_data[standIndexO].planograms[planogramIndexO].scheduled_date = newScheduledDate;
		this.original_data[standIndexO].planograms[planogramIndexO].scheduled_changed = true;

		if (this.stands_to_execute.some(s => s.main_stand_id == event.stand)) {
			this.stands_to_execute.find(s => s.main_stand_id == event.stand)
				.shops.forEach(s => {
				if (s.planogram == event.planogram) {
					s.scheduled_date = newScheduledDate;
				}
			});
		}

		this.updateDatePickerConstraints(i, planogramIndexV);
		this.calculateBulkActionDateRestrictions();
	}

	onSelectDeadline(event, i: number) {
		const standIndexV = this.visible_data.findIndex(s => s.main_stand_id == event.stand),
			planogramIndexV = this.visible_data[standIndexV].planograms.findIndex(p => p.id == event.planogram);

		const standIndexO = this.original_data.findIndex(s => s.main_stand_id == event.stand),
			planogramIndexO = this.original_data[standIndexO].planograms.findIndex(p => p.id == event.planogram);

		const newDeadlineDate = event.date?.toString() || null;

		this.visible_data[standIndexV].planograms[planogramIndexV].deadline_date = newDeadlineDate;
		this.visible_data[standIndexV].planograms[planogramIndexV].deadline_changed = true;
		this.original_data[standIndexO].planograms[planogramIndexO].deadline_date = newDeadlineDate;
		this.original_data[standIndexO].planograms[planogramIndexO].deadline_changed = true;

		if (this.stands_to_execute.some(s => s.main_stand_id == event.stand)) {
			this.stands_to_execute.find(s => s.main_stand_id == event.stand)
				.shops.forEach(s => {
				if (s.planogram == event.planogram) {
					s.deadline_date = newDeadlineDate;
					s.deadline_overdue = newDeadlineDate && Utils.dateDayComparison(new Date(), new Date(newDeadlineDate));
				}
			});
		}

		this.updateDatePickerConstraints(i, planogramIndexV);
		this.calculateBulkActionDateRestrictions();
	}

	private updateDatePickerConstraints(standIndex: number, planogramIndex: number) {
		const planogram = this.visible_data[standIndex].planograms[planogramIndex];
		const tableRow = this.visible_data[standIndex].tableData.data[planogramIndex];

		if (tableRow && tableRow[1]?.data && tableRow[2]?.data) {
			let scheduledMinDate = new Date();
			scheduledMinDate.setDate(scheduledMinDate.getDate() + 1);

			tableRow[1].data = {
				...tableRow[1].data,
				date: planogram.scheduled_date,
				min_date: scheduledMinDate.toString(),
				max_date: planogram.deadline_date ? planogram.deadline_date.toString() : null,
				tooltip: planogram.scheduled_date && !planogram.scheduled_changed ? TOOLTIP_DEADLINE : null
			};

			tableRow[2].data = {
				...tableRow[2].data,
				date: planogram.deadline_date,
				min_date: planogram.scheduled_date ? planogram.scheduled_date.toString() : new Date().toString(),
				tooltip: planogram.deadline_date && !planogram.deadline_changed ? TOOLTIP_DEADLINE : null
			};
		}

		const originalStandIndex = this.original_data.findIndex(s => s.main_stand_id === this.visible_data[standIndex].main_stand_id);
		if (originalStandIndex >= 0 && this.original_data[originalStandIndex].tableData?.data[planogramIndex]) {
			const originalTableRow = this.original_data[originalStandIndex].tableData.data[planogramIndex];
			if (originalTableRow[1]?.data && originalTableRow[2]?.data) {
				let scheduledMinDateOriginal = new Date();
				scheduledMinDateOriginal.setDate(scheduledMinDateOriginal.getDate() + 1);

				originalTableRow[1].data = {
					...originalTableRow[1].data,
					date: planogram.scheduled_date,
					min_date: scheduledMinDateOriginal.toString(),
					max_date: planogram.deadline_date ? planogram.deadline_date.toString() : null,
					tooltip: planogram.scheduled_date && !planogram.scheduled_changed ? TOOLTIP_DEADLINE : null
				};

				originalTableRow[2].data = {
					...originalTableRow[2].data,
					date: planogram.deadline_date,
					min_date: planogram.scheduled_date ? planogram.scheduled_date.toString() : new Date().toString(),
					tooltip: planogram.deadline_date && !planogram.deadline_changed ? TOOLTIP_DEADLINE : null
				};
			}
		}
	}

	private calculateBulkActionDateRestrictions(): void {
		const selectedPlanograms = Utils.to1DArray(this.stands_to_execute.map(s => s.shops)).filter(p => !p.disabled);

		if (selectedPlanograms.length === 0) {
			this.minPossibleScheduledDate = null;
			this.maxPossibleScheduledDate = null;
			this.minPossibleDeadlineDate = null;
			this.maxPossibleDeadlineDate = null;
			this.disableScheduledUpdateAllToggle = true;
			this.disableDeadlineUpdateAllToggle = true;
			return;
		}

		let earliestOverallDeadline: Date | null = null;
		let latestOverallScheduled: Date | null = null;

		for (const p of selectedPlanograms) {
			if (p.deadline_date) {
				const deadline = new Date(p.deadline_date);
				if (!earliestOverallDeadline || deadline < earliestOverallDeadline) {
					earliestOverallDeadline = deadline;
				}
			}
			if (p.scheduled_date) {
				const scheduled = new Date(p.scheduled_date);
				if (!latestOverallScheduled || scheduled > latestOverallScheduled) {
					latestOverallScheduled = scheduled;
				}
			}
		}

		let minScheduled = new Date();
		minScheduled.setDate(minScheduled.getDate() + 1);
		if (latestOverallScheduled && latestOverallScheduled > minScheduled) {
			minScheduled = latestOverallScheduled;
		}
		this.minPossibleScheduledDate = minScheduled;
		this.maxPossibleScheduledDate = earliestOverallDeadline;

		let minDeadline = new Date();
		if (latestOverallScheduled && latestOverallScheduled > minDeadline) {
			minDeadline = latestOverallScheduled;
		}
		this.minPossibleDeadlineDate = minDeadline;
		this.maxPossibleDeadlineDate = null;

		const allScheduledSet = selectedPlanograms.every(p => p.scheduled_date);
		const someScheduledSet = selectedPlanograms.some(p => p.scheduled_date);
		const someDeadlineSet = selectedPlanograms.some(p => p.deadline_date);

		let newDisableScheduledToggle = false;
		if (!allScheduledSet && !someScheduledSet && !someDeadlineSet) {
			newDisableScheduledToggle = false;
		} else if (this.minPossibleScheduledDate && this.maxPossibleScheduledDate && this.minPossibleScheduledDate > this.maxPossibleScheduledDate) {
			newDisableScheduledToggle = true;
		} else if (allScheduledSet && !this.sendExecutionService.scheduled.updateForAll) {
			newDisableScheduledToggle = false;
		} else if (selectedPlanograms.length === 0) {
			newDisableScheduledToggle = true;
		}
		this.disableScheduledUpdateAllToggle = newDisableScheduledToggle;


		const allDeadlineSet = selectedPlanograms.every(p => p.deadline_date);
		let newDisableDeadlineToggle = false;
		if (!allDeadlineSet && !someScheduledSet && !someDeadlineSet) {
			newDisableDeadlineToggle = false;
		} else if (this.minPossibleDeadlineDate && this.maxPossibleDeadlineDate && this.minPossibleDeadlineDate > this.maxPossibleDeadlineDate) {
			newDisableDeadlineToggle = true;
		} else if (allDeadlineSet && !this.sendExecutionService.deadline.updateForAll) {
			newDisableDeadlineToggle = false;
		} else if (selectedPlanograms.length === 0) {
			newDisableDeadlineToggle = true;
		}
		this.disableDeadlineUpdateAllToggle = newDisableDeadlineToggle;

		if (selectedPlanograms.length === 0) {
			this.disableScheduledUpdateAllToggle = true;
			this.disableDeadlineUpdateAllToggle = true;
		}
	}

	onChangeTimeTracker(event, i: number): void {
		const standIndexV = this.visible_data.findIndex(s => s.main_stand_id == event.stand),
			planogramIndexV = this.visible_data[standIndexV].planograms.findIndex(p => p.id == event.planogram);

		const standIndexO = this.original_data.findIndex(s => s.main_stand_id == event.stand),
			planogramIndexO = this.original_data[standIndexO].planograms.findIndex(p => p.id == event.planogram);

		this.visible_data[standIndexV].planograms[planogramIndexV].enable_time_tracking = event.value;
		this.original_data[standIndexO].planograms[planogramIndexO].enable_time_tracking = event.value;

		if (this.stands_to_execute.some(s => s.main_stand_id == event.stand)) {
			this.stands_to_execute.find(s => s.main_stand_id == event.stand)
				.shops.forEach(s => {
				if (s.planogram == event.planogram) {
					s.timeTracker = event.value;
				}
			});
		}

		if (this.visible_data[i] && this.visible_data[i].tableData.data[planogramIndexV] && this.visible_data[i].tableData.data[planogramIndexV][3]?.data) {
			this.visible_data[i].tableData.data[planogramIndexV][3].data.value = event.value;
		}
		if (this.original_data[i] && this.original_data[i].tableData?.data[planogramIndexO] && this.original_data[i].tableData.data[planogramIndexO][3]?.data) {
			this.original_data[i].tableData.data[planogramIndexO][3].data.value = event.value;
		}

		this.checkAllTimeTrackersSelected();
	}

	onSetComment(stand: IStandExecutionData, value: string = ''): void {
		stand.comment = value;
		const executeItem = this.stands_to_execute.find(s => s.main_stand_id == stand.main_stand_id);
		if (executeItem) executeItem.comment = value;
	}

	async apply() {
		if (!this.stands_to_execute.length) return;
		this.showLoader = true;

		let responseData = this.stands_to_execute.map(s => s.shops.map(m => {
			return {
				user_id: m.manager_id,
				stand_id: m.id,
				planogram_id: m.planogram,
				scheduled_date: m.scheduled_date ? this.sessionService.formatDate(m.scheduled_date, 'yyyy-MM-dd'): null,
				deadline_date: m.deadline_date ? this.sessionService.formatDate(m.deadline_date, 'yyyy-MM-dd') : null,
				enable_time_tracking: m.timeTracker,
				comment: s.comment
			};
		}));

		responseData = Utils.to1DArray(responseData);

		await this.planogramsService.bulkExecutions({
			execution_data: responseData
		});

		this.executeEvent.emit({
			standsCount: responseData.length,
			timeTrackerCount: responseData.filter(stand => stand.enable_time_tracking).length,
			scheduleCount: responseData.filter(stand => stand.scheduled_date).length,
			deadlineCount: responseData.filter(stand => stand.deadline_date).length,
			// temp data
			responseData: responseData
		});
	}

	openScheduledModal() {
		this.calculateBulkActionDateRestrictions();

		const selected = Utils
			.to1DArray(this.stands_to_execute.map(s => s.shops))
			.filter(p => !p.disabled);

		const allDeadlinesFromStands = selected
			.filter(p => p.deadline_date)
			.map(p => new Date(p.deadline_date!));

		const allDeadlinesFromData = Utils.to1DArray(this.visible_data.map(s => s.planograms))
			.filter(p => p.deadline_date)
			.map(p => new Date(p.deadline_date!));

		const allDeadlines = [...allDeadlinesFromStands, ...allDeadlinesFromData];
		const earliestDeadlineAll =
			allDeadlines.length ? new Date(Math.min(...allDeadlines.map(d => +d))) : null;

		const unscheduledDeadlinesFromStands = selected
			.filter(p => !p.scheduled_date && p.deadline_date)
			.map(p => new Date(p.deadline_date!));

		const unscheduledDeadlinesFromData = Utils.to1DArray(this.visible_data.map(s => s.planograms))
			.filter(p => !p.scheduled_date && p.deadline_date)
			.map(p => new Date(p.deadline_date!));

		const unscheduledDeadlines = [...unscheduledDeadlinesFromStands, ...unscheduledDeadlinesFromData];
		const earliestDeadlineUnscheduled =
			unscheduledDeadlines.length ? new Date(Math.min(...unscheduledDeadlines.map(d => +d))) : null;

		let effectiveDeadlineAll = earliestDeadlineAll;
		let effectiveDeadlineUnscheduled = earliestDeadlineUnscheduled;

		let minDateObj: Date;
		if (this.sendExecutionService.scheduled.updateForAll) {
			minDateObj = this.minPossibleScheduledDate
				? new Date(this.minPossibleScheduledDate)
				: (() => {
					const d = new Date();
					d.setDate(d.getDate() + 1);
					return d;
				})();
		} else {
			minDateObj = new Date();
			minDateObj.setDate(minDateObj.getDate() + 1);
		}

		const dialogRef = this.dialog.open<ComplexDeadlineModalComponent, IUniversalDateModalData>(ComplexDeadlineModalComponent, {
			backdropClass: 'back-drop',
			width: '40%',
			autoFocus: false,
			data: {
				mode: 'scheduled',
				state: this.sendExecutionService.scheduled.date,
				minDate: minDateObj.toString(),
				maxDateAll: effectiveDeadlineAll?.toString() ?? null,
				maxDateUns: effectiveDeadlineUnscheduled?.toString() ?? null,
				title: 'SCHEDULED_PLANOGRAMS.TITLE',
				toggleLabel: 'SCHEDULED_PLANOGRAMS.INFO_LABEL',
				dateInputLabel: 'SCHEDULED_PLANOGRAMS.DATE',
				datepickerPlaceholder: 'SCHEDULED_PLANOGRAMS.IN_TIME',
				info: 'SCHEDULED_PLANOGRAMS.SCHEDULED_MODULE',
				presets: DEFAULTS_PRESETS_SCHEDULED
			}
		});

		dialogRef.afterClosed().subscribe(result => {
			if (!result) return;

			this.sendExecutionService.scheduled = {
				date: result.date,
				updateForAll: result.updateForAll
			};

			const filterData = (date, isValid) => result.updateForAll ? isValid : isValid && !date;

			this.visible_data.forEach(stand => {
				stand.planograms
					.filter(p => filterData(p.scheduled_date, !p.disabled))
					.forEach(p => {
						p.scheduled_date = result.date?.toString() || null;
						p.scheduled_changed = true;
					});

				stand.tableData.data
					.filter(row => filterData(row[1]?.data?.date, !row.disabled))
					.forEach(row => {
						if (row[1]?.data) {
							row[1].data.date = result.date?.toString() || null;
							row[1].data.tooltip = null;
						}
					});
			});

			this.original_data.forEach(stand => {
				stand.planograms.forEach(p => {
					if (filterData(p.scheduled_date, !p.disabled)) {
						p.scheduled_date = result.date?.toString() || null;
						p.scheduled_changed = true;
					}
				});

				if (stand.tableData?.data) {
					stand.tableData.data.forEach(row => {
						if (filterData(row[1]?.data?.date, !row.disabled) && row[1]?.data) {
							row[1].data.date = result.date?.toString() || null;
							row[1].data.tooltip = null;
						}
					});
				}
			});

			this.stands_to_execute.forEach(stand =>
				stand.shops
					.filter(shop => filterData(shop.scheduled_date, true))
					.forEach(shop => {
						shop.scheduled_date = result.date?.toString() || null;
					})
			);

			this.visible_data.forEach((stand, standIndex) => {
				stand.planograms.forEach((planogram, planogramIndex) => {
					this.updateDatePickerConstraints(standIndex, planogramIndex);
				});
			});

			this.calculateBulkActionDateRestrictions();
		});
	}

	openDeadlineModal(): void {
		this.calculateBulkActionDateRestrictions();

		const selected = Utils.to1DArray(this.stands_to_execute.map(s => s.shops));

		const scheduledAllFromStands = selected
			.filter(p => p.scheduled_date)
			.map(p => new Date(p.scheduled_date!));

		const scheduledAllFromData = Utils.to1DArray(this.visible_data.map(s => s.planograms))
			.filter(p => p.scheduled_date)
			.map(p => new Date(p.scheduled_date!));

		const scheduledAll = [...scheduledAllFromStands, ...scheduledAllFromData];
		const latestScheduledAll = scheduledAll.length ? new Date(Math.max(...scheduledAll.map(d => +d))) : null;

		const scheduledUnsFromStands = selected
			.filter(p => !p.deadline_date && p.scheduled_date)
			.map(p => new Date(p.scheduled_date!));

		const scheduledUnsFromData = Utils.to1DArray(this.visible_data.map(s => s.planograms))
			.filter(p => !p.deadline_date && p.scheduled_date)
			.map(p => new Date(p.scheduled_date!));

		const scheduledUns = [...scheduledUnsFromStands, ...scheduledUnsFromData];
		const latestScheduledUns = scheduledUns.length ? new Date(Math.max(...scheduledUns.map(d => +d))) : null;

		const today = new Date();

		let effectiveScheduledAll = latestScheduledAll;
		let effectiveScheduledUns = latestScheduledUns;
		
		const minDateAll = effectiveScheduledAll && effectiveScheduledAll > today ? effectiveScheduledAll : today;
		const minDateUns = effectiveScheduledUns && effectiveScheduledUns > today ? effectiveScheduledUns : today;

		const minDateForModal = this.sendExecutionService.deadline.updateForAll ? minDateAll : minDateUns;
		let maxDateForModal: Date | null = null;
		let disableToggle = false;

		if (selected.length === 0) {
			disableToggle = true;
		} else {
			const allHaveDeadline = selected.every(p => p.deadline_date);
			if (allHaveDeadline && !this.sendExecutionService.deadline.updateForAll) {
				disableToggle = true;
			}

			if (!this.sendExecutionService.deadline.updateForAll) {
				if (this.maxPossibleDeadlineDate) maxDateForModal = this.maxPossibleDeadlineDate;
				if (maxDateForModal && maxDateForModal < minDateForModal) disableToggle = true;
			}
		}
		this.disableDeadlineUpdateAllToggle = disableToggle;

		const dialogRef = this.dialog.open<ComplexDeadlineModalComponent, IUniversalDateModalData>(ComplexDeadlineModalComponent, {
			backdropClass: 'back-drop',
			width: '40%',
			autoFocus: false,
			data: {
				mode: 'deadline',
				state: this.sendExecutionService.deadline.date,
				minDate: minDateForModal.toString(),
				minDateAll: minDateAll.toString(),
				minDateUns: minDateUns.toString(),
				maxDate: maxDateForModal?.toString() ?? null,
				title: 'DEADLINE.MODAL.TITLE',
				toggleLabel: 'DEADLINE.MODAL.UPDATE_FOR_ALL',
				dateInputLabel: 'DEADLINE.MODAL.DOING_TO',
				datepickerPlaceholder: 'NO_LIMIT_2',
				info: 'SCHEDULED_PLANOGRAMS.DEADLINE_MODULE'
			}
		});

		dialogRef.afterClosed().subscribe(result => {
			if (!result) return;

			this.sendExecutionService.deadline = {
				date: result.date,
				updateForAll: result.updateForAll
			};

			const filterData = (date, isValid) => result.updateForAll ? isValid : isValid && !date;

			this.visible_data.forEach(stand => {
				stand.planograms
					.filter(p => filterData(p.deadline_date, !p.disabled))
					.forEach(p => {
						p.deadline_date = result.date?.toString() || null;
						p.deadline_changed = true;
					});

				stand.tableData.data
					.filter(row => filterData(row[2]?.data?.date, !row.disabled))
					.forEach(row => {
						if (row[2]?.data) {
							row[2].data.date = result.date?.toString() || null;
							row[2].data.tooltip = null;
						}
					});
			});

			this.original_data.forEach(stand => {
				stand.planograms.forEach(p => {
					if (filterData(p.deadline_date, !p.disabled)) {
						p.deadline_date = result.date?.toString() || null;
						p.deadline_changed = true;
					}
				});

				stand.tableData?.data.forEach(row => {
					if (filterData(row[2]?.data?.date, !row.disabled) && row[2]?.data) {
						row[2].data.date = result.date?.toString() || null;
						row[2].data.tooltip = null;
					}
				});
			});

			this.stands_to_execute.forEach(stand =>
				stand.shops
					.filter(shop => filterData(shop.deadline_date, true))
					.forEach(shop => {
						shop.deadline_date = result.date?.toString() || null;
						shop.deadline_overdue = result.date && Utils.dateDayComparison(new Date(), new Date(result.date));
					})
			);

			this.visible_data.forEach((s, si) =>
				s.planograms.forEach((_, pi) => this.updateDatePickerConstraints(si, pi))
			);

			this.calculateBulkActionDateRestrictions();
		});
	}

	setAllTimeTracker(value: boolean): void {
		this.visible_data.forEach(stand => {
			stand.planograms.forEach(planogram => {
				const isDisabled = (!planogram.shop_id || !planogram.managers || !planogram.managers.length || planogram.managers.some(m => !m.id) || !planogram.planogram_active);
				if (!isDisabled) planogram.enable_time_tracking = value;
			});

			stand.tableData.data.forEach(row => {
				if (!row.disabled && row[3]?.data) {
					row[3].data.value = value;
				}
			});
		});

		this.original_data.forEach(stand => {
			stand.planograms.forEach(planogram => {
				const isDisabled = (!planogram.shop_id || !planogram.managers || !planogram.managers.length || planogram.managers.some(m => !m.id) || !planogram.planogram_active);
				if (!isDisabled) planogram.enable_time_tracking = value;
			});

			if (stand.tableData?.data) {
				stand.tableData.data.forEach(row => {
					if (!row.disabled && row[3]?.data) {
						row[3].data.value = value;
					}
				});
			}
		});

		this.stands_to_execute.forEach(stand => {
			stand.shops.forEach(shop => {
				shop.timeTracker = value;
			});
		});
	}

	toggleTimeTracker(): void {
		this.isTimeTrackerActive = !this.isTimeTrackerActive;
		this.setAllTimeTracker(this.isTimeTrackerActive);
	}

	checkAllTimeTrackersSelected(): void {
		const enabledPlanograms = Utils.to1DArray(
			this.visible_data.map(stand =>
				stand.planograms.filter(planogram => !planogram.disabled)
			)
		);

		this.isTimeTrackerActive = enabledPlanograms.length > 0 &&
			enabledPlanograms.every(planogram => planogram.enable_time_tracking);
	}

	cancel() {
		this.cancelEvent.emit(false);
	}

	ngOnDestroy() {
		this.sendExecutionService.clearData();
	}
}